//================================================================
//  Component: Subnets Allocation Guide
//================================================================

//  Purpose: Displays table that subnet admins can edit. Document is stored in subnets collection with document id of 'allocationguide'

//  Properties:

//  Example:
//    <SubnetsAllocationGuide></SubnetsAllocationGuide>    

//================================================================


// Libraries
import React, { useContext, useState, useReducer, useEffect } from 'react';

// Context
import { GetUser, GetRoles, SetToast } from '../../../../../Library/GlobalContexts';

// Functions
import DocumentListener from '../../../../../Library/DocumentListener';
import CheckInventoryRole from '../../../../../Library/CheckInventoryRole';
import WriteDocument from '../../../../../Library/WriteDocument';

// Components
import TableCell from './TableCell';

// Images
import LoadingIcon from '../../../../../Components/Images/Image_Loading_Ripple.svg';
import ErrorIcon from '../../../../../Components/Images/Splash/Error_Fatal.svg'
import infoIcon from '../../../../../Components/Images/Icon_InfoToast_Blue.svg';
import addIcon from '../../../../../Components/Images/Icon_Add_Teal.svg';
import deletIcon from '../../../../../Components/Images/Icon_DeleteCircle_Red.svg';


export default function SubnetsAllocationGuide() {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error'
    const [paneStatus, setPaneStatus] = useState('pending');

    const [roleStatus, setRoleStatus] = useState('download'); // download, write & admin

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the current allocation State for this pane
    const [allocationState, setAllocationState] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'allocationguideDoc': {},       // Holds the allocation document e.g. documentid: 'allocationguide'
            'rows': [],                     // Holds the rows, contained within the allocation doc
            'preventSave': true,            // Tracks if a change was made

            'selectedRowIndex': -1,         // Select row index

            'paneOpen': false,             // Open and close the allocation pane
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handle, adding a row into the useReducer
    //  - Triggered by the user selecting 'add icon' on a table row
    //  - Row will be added AT the index, hence the array is spliced
    function AddRow(index) {

        if (index === undefined) return;

        // New row object
        const newRow = {
            'region': '',
            'regionheight': '100px',
            'description': '',
            'descriptionheight': '100px',
            'vlan': '',
            'vlanheight': '100px',
            'guide': '',
            'guideheight': '100px',
        };

        // If index is 0, append first element in array
        if (index === 0) {

            setAllocationState({
                'rows': [newRow, ...allocationState.rows],
                'preventSave': false,
            });

            return;

        }

        // Splice into array based off index
        allocationState.rows.splice(index, 0, newRow)
        setAllocationState({
            'rows': [...allocationState.rows],
            'preventSave': false,
        });

    }

    // Handle, deleting a row from the useReducer
    //  - Triggered by the user selecting 'delete icon' on a table row
    function DeleteRow(index) {

        if (index === undefined) return;

        // Splice from array based off index
        allocationState.rows.splice(index, 1)
        setAllocationState({
            'rows': [...allocationState.rows],
            'preventSave': false,
        });

    }

    // Handling saving the cell, when user selects the 'Save' button
    function handleSave() {

        const allocationDoc = {
            'documentid': 'allocationguide',
            'rows': allocationState.rows,
            'createddate': new Date(),
            'lastmodifieddate': new Date(),
            'lastmodifiedby': {
                'email': getUser.emailaddress,
                'givenname': getUser.givenname,
                'surname': getUser.surname,
            },
        }

        WriteDocument('subnets', 'allocationguide', allocationDoc, true).then(() => {

            // Show Success Toast
            setToast(
                {
                    'type': 'success',
                    'message': 'IPAM allocation guide updated.'
                }
            );

            // Reset pane
            setAllocationState({
                'paneOpen': false,
                'preventSave': true,
                'selectedRowIndex': -1,
            });

            setPaneStatus('pending');

        }).catch((error) => {

            console.log(error);

            // Set error on toast
            setToast({
                'type': 'error',
                'message': 'Failed to update IPAM Allocation Guide',
            });

        });

    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    useEffect(() => {

        if (getUser === undefined) return;
        if (getRoles === undefined) return;
        if (allocationState.paneOpen === false) return;

        // ============================================
        //  Perform role check
        // ============================================

        const roleStatus = CheckInventoryRole('subnets', getRoles, getUser);
        setRoleStatus(roleStatus);

        // ============================================
        //  Fetch Data
        // ============================================

        function onLoad(document) {

            // First time, assuming no document is found!
            if (document === undefined) {

                const allocationDoc = {
                    'documentid': 'allocationguide',
                    'rows': [],
                    'createddate': new Date(),
                    'createdby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                }

                WriteDocument('subnets', 'allocationguide', allocationDoc, false).then(() => {

                    setAllocationState({
                        'allocationguideDoc': allocationDoc,
                        'rows': [],
                    });

                    setPaneStatus('onload');

                }).catch((error) => {

                    console.log(`Failed to create 'allocationguide' document, ${error}`);
                    setPaneStatus('error-fatal');

                });

            }

            // Existing document found, update useReducer
            setAllocationState({
                'allocationguideDoc': document,
                'rows': document.rows,
            });


            setPaneStatus('onload');

        }

        function onChange(document) {

            setAllocationState({
                'allocationDocument': document
            });

        }

        function onError(error) {

            console.log(error);
            setPaneStatus('error-fatal');

        }

        const unsubscribe = DocumentListener('subnets', 'allocationguide', onLoad, onChange, onError);

        return () => {
            unsubscribe();
        };

    }, [getUser, getRoles, allocationState.paneOpen]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (allocationState.paneOpen === false) {

        return (
            <div className='flex flex-row items-center bg-[#E1F4FF] px-[10px] m-1 rounded-md gap-2 font-medium cursor-pointer shadow-sm hover:bg-[#e1ebff]' onClick={() => setAllocationState({ 'paneOpen': true })}>
                <img className='w-[25px] h-[25px]' src={infoIcon} alt='Info-Icon'></img>
                <span className='cursor-pointer'>Allocation Guide</span>
            </div>
        )

    }

    // ---------------------------------------------------
    //  Pending
    // ---------------------------------------------------

    if (paneStatus === 'pending') {

        return (

            <>

                {/* ======== Keeps the button is visible during slide animation ========= */}
                <div className='flex flex-row items-center bg-[#E1F4FF] px-[10px] m-1 rounded-md gap-2 font-medium cursor-pointer shadow-sm hover:bg-[#e1ebff]' onClick={() => setAllocationState({ 'paneOpen': true })}>
                    <img className='w-[25px] h-[25px]' src={infoIcon} alt='Info-Icon'></img>
                    <span className='cursor-pointer'>Allocation Guide</span>
                </div>


                {/* Pending pane */}
                <div className='Pane-Background'>

                    <dialog className='Pane-AllocationGuide flex flex-col justify-center'>

                        <img className='w-[200px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                        <button className='Primary-Button w-fit self-center' onClick={() => {

                                // Reset pane
                                setAllocationState({
                                    'rows': [],
                                    'paneOpen': false,
                                    'preventSave': true,
                                    'selectedRowIndex': -1,
                                });

                                // Reset status
                                setPaneStatus('pending');
                        
                        }}>
                            Cancel
                        </button>

                    </dialog>

                </div>

            </>

        )

    }

    // ---------------------------------------------------
    //  Onload
    // ---------------------------------------------------

    else if (paneStatus === 'onload') {

        return (

            <>

                {/* ======== Keeps the button is visible during slide animation ========= */}
                <div className='flex flex-row items-center bg-[#E1F4FF] px-[10px] m-1 rounded-md gap-2 font-medium cursor-pointer shadow-sm hover:bg-[#e1ebff]' onClick={() => setAllocationState({ 'paneOpen': true })}>
                    <img className='w-[25px] h-[25px]' src={infoIcon} alt='Info-Icon'></img>
                    <span className='cursor-pointer'>Allocation Guide</span>
                </div>


                <div className='Pane-Background'>

                    <dialog className='Pane-AllocationGuide flex flex-col py-[20px] px-[20px]'>

                        {/* ----------------------------------- */}
                        {/*  Table Container                    */}
                        {/* ----------------------------------- */}

                        <div className='Table-Container flex flex-col w-full h-full overflow-hidden'>

                            {/* ======== Header ========== */}
                            <div className='flex flex-row justify-between py-[15px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                                <label className='font-semibold self-center'>
                                    IPAM Allocation Guide
                                </label>

                            </div>

                            {/* ======== Table Scrollable ========== */}
                            <div className='w-full overflow-auto'>

                                <table className='w-full'>
                                    <thead>
                                        <tr className='sticky top-0 bg-white border-b-[#D8D8D8] border-solid border-b-2 z-10 shadow-sm overflow-visible'>
                                            <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap w-[15vw]`}>
                                                Region
                                            </th>
                                            <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap w-[15vw]`}>
                                                VRF / VPC / Zone …
                                            </th>
                                            <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap w-[15vw]`}>
                                                Vlan
                                            </th>
                                            <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap w-[15vw]`}>
                                                Guide
                                            </th>

                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            // No allocation guides found

                                            allocationState.rows.length === 0 ?
                                                (
                                                    <tr>
                                                        <td colSpan={4}>
                                                            <div className='text-center p-[2%]'>
                                                                No allocation guides found...
                                                                <br></br><br></br>
                                                                Click <span className='underline text-[#0972D3] cursor-pointer' onClick={() => AddRow(0)}>here</span> to add a row.
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                                :
                                                (
                                                    // Map in each commercial item
                                                    allocationState.rows.map((row, index) => (

                                                        <>

                                                            <tr key={index} className={`border-solid border-b ${allocationState.selectedRowIndex === index && 'border-b-[#00a7a4] border-b'}`} onClick={() => roleStatus === 'admin' && setAllocationState({ 'selectedRowIndex': index })}>
                                                                <td className={`align-top border-[#D8D8D8] border-solid border-r ${allocationState.selectedRowIndex === index && ''}`}>
                                                                    <TableCell
                                                                        index={index}
                                                                        row={row}
                                                                        objectKey='region'
                                                                        heightKey='regionheight'
                                                                        allocationState={allocationState}
                                                                        setAllocationState={setAllocationState}
                                                                        disabled={roleStatus !== 'admin'}
                                                                    ></TableCell>
                                                                </td>
                                                                <td className='align-top border-[#D8D8D8] border-solid border-r'>
                                                                    <TableCell
                                                                        index={index}
                                                                        row={row}
                                                                        objectKey='description'
                                                                        heightKey='descriptionheight'
                                                                        allocationState={allocationState}
                                                                        setAllocationState={setAllocationState}
                                                                        disabled={roleStatus !== 'admin'}
                                                                    ></TableCell>
                                                                </td>
                                                                <td className='align-top border-[#D8D8D8] border-solid border-r'>
                                                                    <TableCell
                                                                        index={index}
                                                                        row={row}
                                                                        objectKey='vlan'
                                                                        heightKey='vlanheight'
                                                                        allocationState={allocationState}
                                                                        setAllocationState={setAllocationState}
                                                                        disabled={roleStatus !== 'admin'}
                                                                    ></TableCell>
                                                                </td>
                                                                <td className='align-top'>
                                                                    <TableCell
                                                                        index={index}
                                                                        row={row}
                                                                        objectKey='guide'
                                                                        heightKey='guideheight'
                                                                        allocationState={allocationState}
                                                                        setAllocationState={setAllocationState}
                                                                        disabled={roleStatus !== 'admin'}
                                                                    ></TableCell>
                                                                </td>

                                                            </tr>

                                                            {/* Add or Delete row buttons */}
                                                            <tr hidden={!(allocationState.selectedRowIndex === index)}>

                                                                <td colSpan={4} className=' border-[#D8D8D8] border-solid border-b'>
                                                                    <div className='flex flex-row gap-1'>
                                                                        <img className='w-[20px] cursor-pointer hover:scale-105' src={addIcon} alt='add-button' onClick={() => AddRow(index + 1)}></img>
                                                                        <img className='w-[20px] cursor-pointer hover:scale-105' src={deletIcon} alt='delete-button' onClick={() => DeleteRow(index)}></img>
                                                                    </div>

                                                                </td>

                                                            </tr>

                                                        </>

                                                    ))
                                                )
                                        }
                                    </tbody>

                                </table>

                            </div>

                        </div>

                        {/* Button Container */}
                        <div className='flex flex-row gap-2'>

                            {/* Save Button */}
                            <button hidden={roleStatus !== 'admin'} disabled={allocationState.preventSave} className='Primary-Button w-fit' onClick={() => handleSave()}>
                                Save
                            </button>

                            {/* Close Button */}
                            <button className='Secondary-Button w-fit' onClick={() => {

                                // Reset pane
                                setAllocationState({
                                    'rows': [],
                                    'paneOpen': false,
                                    'preventSave': true,
                                    'selectedRowIndex': -1,
                                });

                                // Reset status
                                setPaneStatus('pending');

                            }}>
                                Close
                            </button>
                        </div>

                    </dialog>

                </div>

            </>


        )

    }

    // ---------------------------------------------------
    //  Error
    // ---------------------------------------------------

    else if (paneStatus === 'error') {

        return (

            <>

                {/* ======== Keeps the button is visible during slide animation ========= */}
                <div className='flex flex-row items-center bg-[#E1F4FF] px-[10px] m-1 rounded-md gap-2 font-medium cursor-pointer shadow-sm hover:bg-[#e1ebff]' onClick={() => setAllocationState({ 'paneOpen': true })}>
                    <img className='w-[25px] h-[25px]' src={infoIcon} alt='Info-Icon'></img>
                    <span className='cursor-pointer'>Allocation Guide</span>
                </div>


                {/* Failed pane */}
                <div className='Pane-Background'>

                    <dialog className='Pane-AllocationGuide flex flex-col justify-center items-center'>

                        <img src={ErrorIcon} alt='Error'></img>
                        <h4>Oops! Something went wrong.</h4>
                        <p className='leading-[1.7]'>
                            There was an error loading this page.
                        </p>
                        <button className='Primary-Button w-fit' onClick={() => {

                            // Reset pane
                            setAllocationState({
                                'rows': [],
                                'paneOpen': false,
                                'preventSave': true,
                                'selectedRowIndex': -1,
                            });

                            // Reset status
                            setPaneStatus('pending');

                        }}>Back</button>

                    </dialog>

                </div>
            </>

        )

    }

    //------------------------------------------------------

}
